import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { 
  Building2, Upload, Globe, Facebook, Instagram, Twitter, 
  Users, Percent, PoundSterling, QrCode, MessageCircle, 
  CheckCircle, AlertCircle, ArrowRight, Star, Video, Image 
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BusinessApplication = ({ isOpen, onClose, user }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState({});
  
  const [formData, setFormData] = useState({
    // Basic Information (matching your form)
    date: new Date().toISOString().split('T')[0],
    business_name: '',
    email_address: '',
    address: '',
    postcode: '',
    main_contact: '',
    mobile_phone: '',
    
    // Business Details
    type_of_services: '',
    type_of_products: '',
    target_audience: '',
    special_instructions: '',
    
    // Discount Options (matching your form exactly)
    discount_student: false,
    discount_pensioner: false,
    discount_trader: false,
    discount_gov_workers: false,
    discount_residents: false,
    discount_visitors: false,
    
    // Discount Types
    discount_type: 'percentages', // percentages, flat_rate, specific
    discount_details: '',
    
    // Package Selection (matching your prices)
    selected_package: '',
    package_amount: 0,
    
    // Additional Services
    description_of_work: '',
    url_name: '',
    
    // Video Options
    include_video: 'no', // yes, no, url_link_only
    video_design: false, // £25.99 option
    
    // Window Posters
    window_posters: '1', // 1 (included), 2 (£2.99), 3 (£5.99)
    
    // Payment Details
    payment_method: '',
    sales_executive_ref: '',
    
    // Generated
    generated_url: '',
    epa_number: ''
  });

  // Package options matching your form
  const packageOptions = [
    { id: 'standard', name: 'Standard Package', amount: 130.00 },
    { id: 'deluxe', name: 'Deluxe Package', amount: 190.00 }, 
    { id: 'platinum', name: 'Platinum Package', amount: 290.00 },
    { id: 'bespoke', name: 'Bespoke Package Quotation', amount: 'TBQ' }
  ];

  // Discount customer types (matching your form)
  const discountTypes = [
    { id: 'student', label: 'Student', field: 'discount_student' },
    { id: 'pensioner', label: 'Pensioner', field: 'discount_pensioner' },
    { id: 'trader', label: 'Trader', field: 'discount_trader' },
    { id: 'gov_workers', label: 'Gov/Workers', field: 'discount_gov_workers' },
    { id: 'residents', label: 'Residents', field: 'discount_residents' },
    { id: 'visitors', label: 'Visitors', field: 'discount_visitors' }
  ];

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        setCategories(response.data.categories);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };

    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-generate URL when business name changes
    if (field === 'business_name' && value) {
      const urlName = value.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 20);
      setFormData(prev => ({ ...prev, generated_url: `${urlName}.shoppersdiscount.club` }));
    }
    
    // Update package amount when package changes
    if (field === 'selected_package') {
      const selectedPkg = packageOptions.find(pkg => pkg.id === value);
      setFormData(prev => ({ ...prev, package_amount: selectedPkg?.amount || 0 }));
    }
  };

  const handleDiscountToggle = (field) => {
    setFormData(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const calculateTotal = () => {
    let total = typeof formData.package_amount === 'number' ? formData.package_amount : 0;
    
    // Add video design cost
    if (formData.video_design) {
      total += 25.99;
    }
    
    // Add window poster costs
    if (formData.window_posters === '2') {
      total += 2.99;
    } else if (formData.window_posters === '3') {
      total += 5.99;
    }
    
    return total;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      // Create the business application with all form data
      const applicationData = {
        ...formData,
        user_id: user?.id,
        application_status: 'pending',
        total_amount: calculateTotal()
      };

      const response = await axios.post(`${API}/business-applications`, applicationData, {
        headers: { 
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        }
      });

      // Success - show confirmation
      setCurrentStep(4); // Success step
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Application submission failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextStep = () => {
    if (currentStep < 3) setCurrentStep(prev => prev + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(prev => prev - 1);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <Card className="border-0 shadow-none">
          <CardHeader className="bg-gradient-to-r from-green-600 to-orange-500 text-white">
            <div className="flex justify-between items-center">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_be48effd-3c36-477f-be09-01d2f597ad9e/artifacts/3m4p66qd_shoppersdiscountlogo%20large-edit-20231028192141.jpg" 
                    alt="Shoppers Discount Club" 
                    className="h-12 w-12 rounded-full bg-white p-1"
                  />
                  <div>
                    <CardTitle className="text-2xl font-bold">SHOPPERS DISCOUNT CLUB</CardTitle>
                    <p className="text-white/90">Live Instant Discount - The Next Big Thing</p>
                  </div>
                </div>
                <p className="text-sm text-white/80">ORDER FORM</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="text-white hover:bg-white/20"
              >
                ✕
              </Button>
            </div>
            
            {/* Progress Steps */}
            <div className="flex items-center justify-center space-x-4 mt-6">
              {[1, 2, 3].map((step) => (
                <div key={step} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    currentStep >= step ? 'bg-white text-green-600' : 'bg-white/30 text-white'
                  }`}>
                    {currentStep > step ? <CheckCircle className="h-5 w-5" /> : step}
                  </div>
                  {step < 3 && (
                    <div className={`w-8 h-0.5 ${currentStep > step ? 'bg-white' : 'bg-white/30'}`}></div>
                  )}
                </div>
              ))}
            </div>
            
            <div className="text-center mt-2 text-white/90 text-sm">
              {currentStep === 1 && "Business Information & Contact Details"}
              {currentStep === 2 && "Services, Discounts & Package Selection"} 
              {currentStep === 3 && "Review & Submit Application"}
              {currentStep === 4 && "Application Complete"}
            </div>
          </CardHeader>

          <CardContent className="p-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md mb-6 flex items-center">
                <AlertCircle className="h-5 w-5 mr-2" />
                {error}
              </div>
            )}

            {/* Step 1: Basic Information */}
            {currentStep === 1 && (
              <div className="space-y-6">
                {/* Top Row - Date, Business Name, Email */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="date">DATE</Label>
                    <Input
                      id="date"
                      type="date"
                      value={formData.date}
                      onChange={(e) => handleInputChange('date', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="business_name">BUSINESS NAME *</Label>
                    <Input
                      id="business_name"
                      placeholder="Enter your business name"
                      value={formData.business_name}
                      onChange={(e) => handleInputChange('business_name', e.target.value)}
                      className="mt-1"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="email_address">EMAIL ADDRESS *</Label>
                    <Input
                      id="email_address"
                      type="email"
                      placeholder="business@example.com"
                      value={formData.email_address}
                      onChange={(e) => handleInputChange('email_address', e.target.value)}
                      className="mt-1"
                      required
                    />
                  </div>
                </div>

                {/* Second Row - Address, Postcode */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="address">ADDRESS *</Label>
                    <Input
                      id="address"
                      placeholder="Business address"
                      value={formData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      className="mt-1"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="postcode">POSTCODE *</Label>
                    <Input
                      id="postcode"
                      placeholder="SW1A 1AA"
                      value={formData.postcode}
                      onChange={(e) => handleInputChange('postcode', e.target.value)}
                      className="mt-1"
                      required
                    />
                  </div>
                </div>

                {/* Third Row - Main Contact, Mobile Phone */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="main_contact">MAIN CONTACT *</Label>
                    <Input
                      id="main_contact"
                      placeholder="Contact person name"
                      value={formData.main_contact}
                      onChange={(e) => handleInputChange('main_contact', e.target.value)}
                      className="mt-1"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="mobile_phone">MOBILE PHONE *</Label>
                    <Input
                      id="mobile_phone"
                      placeholder="07XXX XXXXXX"
                      value={formData.mobile_phone}
                      onChange={(e) => handleInputChange('mobile_phone', e.target.value)}
                      className="mt-1"
                      required
                    />
                  </div>
                </div>

                {/* Business Details Row */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label htmlFor="type_of_services">TYPE OF SERVICES</Label>
                    <Textarea
                      id="type_of_services"
                      placeholder="Describe your services"
                      value={formData.type_of_services}
                      onChange={(e) => handleInputChange('type_of_services', e.target.value)}
                      className="mt-1 min-h-[80px]"
                    />
                  </div>

                  <div>
                    <Label htmlFor="type_of_products">TYPE OF PRODUCTS</Label>
                    <Textarea
                      id="type_of_products"
                      placeholder="Describe your products"
                      value={formData.type_of_products}
                      onChange={(e) => handleInputChange('type_of_products', e.target.value)}
                      className="mt-1 min-h-[80px]"
                    />
                  </div>

                  <div>
                    <Label htmlFor="target_audience">TARGET AUDIENCE</Label>
                    <Textarea
                      id="target_audience"
                      placeholder="Who are your customers?"
                      value={formData.target_audience}
                      onChange={(e) => handleInputChange('target_audience', e.target.value)}
                      className="mt-1 min-h-[80px]"
                    />
                  </div>

                  <div>
                    <Label htmlFor="special_instructions">SPECIAL INSTRUCTIONS</Label>
                    <Textarea
                      id="special_instructions"
                      placeholder="Any special requirements"
                      value={formData.special_instructions}
                      onChange={(e) => handleInputChange('special_instructions', e.target.value)}
                      className="mt-1 min-h-[80px]"
                    />
                  </div>
                </div>

                {/* Generated URL Preview */}
                {formData.business_name && (
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <div className="flex items-center space-x-2 mb-2">
                      <QrCode className="h-5 w-5 text-green-600" />
                      <span className="font-semibold text-green-600">Your Generated Chatbot URL:</span>
                    </div>
                    <Badge variant="outline" className="text-green-600 border-green-600">
                      https://{formData.generated_url}
                    </Badge>
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Discounts & Packages */}
            {currentStep === 2 && (
              <div className="space-y-6">
                {/* Discount Types Section */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 text-green-600">DISCOUNT TO OFFER</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                    {discountTypes.map((discount) => (
                      <div key={discount.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={discount.field}
                          checked={formData[discount.field]}
                          onCheckedChange={() => handleDiscountToggle(discount.field)}
                        />
                        <Label htmlFor={discount.field} className="font-medium cursor-pointer">
                          {discount.label}
                        </Label>
                      </div>
                    ))}
                  </div>

                  {/* Discount Type Selection */}
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>Discount Method</Label>
                      <Select value={formData.discount_type} onValueChange={(value) => handleInputChange('discount_type', value)}>
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select method" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="percentages">Percentages</SelectItem>
                          <SelectItem value="flat_rate">Flat Rate</SelectItem>
                          <SelectItem value="specific">Specific</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="md:col-span-2">
                      <Label htmlFor="discount_details">Discount Details</Label>
                      <Input
                        id="discount_details"
                        placeholder="e.g. 15% off all services, £5 off purchases over £20"
                        value={formData.discount_details}
                        onChange={(e) => handleInputChange('discount_details', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>

                {/* Package Selection */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 text-green-600">12 MONTHS PACKAGE PURCHASED</h3>
                  <div className="space-y-3">
                    {packageOptions.map((pkg) => (
                      <div key={pkg.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                        <div className="flex items-center space-x-3">
                          <input
                            type="radio"
                            id={pkg.id}
                            name="package"
                            value={pkg.id}
                            checked={formData.selected_package === pkg.id}
                            onChange={(e) => handleInputChange('selected_package', e.target.value)}
                            className="h-4 w-4 text-green-600"
                          />
                          <Label htmlFor={pkg.id} className="font-medium cursor-pointer">
                            {pkg.name}
                          </Label>
                        </div>
                        <div className="text-lg font-bold text-green-600">
                          {typeof pkg.amount === 'number' ? `£${pkg.amount.toFixed(2)}` : pkg.amount}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Description of Work */}
                <div>
                  <Label htmlFor="description_of_work">DESCRIPTION OF WORK</Label>
                  <Textarea
                    id="description_of_work"
                    placeholder="Describe what you would like us to include in your chatbot and marketing materials"
                    value={formData.description_of_work}
                    onChange={(e) => handleInputChange('description_of_work', e.target.value)}
                    className="mt-1 min-h-[100px]"
                  />
                </div>

                {/* URL Name */}
                <div>
                  <Label htmlFor="url_name">WHAT NAME WOULD YOU LIKE ON URL:</Label>
                  <Input
                    id="url_name"
                    placeholder="e.g. yourbusinessname (will create yourbusinessname.shoppersdiscount.club)"
                    value={formData.url_name}
                    onChange={(e) => handleInputChange('url_name', e.target.value)}
                    className="mt-1"
                  />
                </div>

                {/* Video Options */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 text-green-600">VIDEO OPTIONS</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="video_yes"
                        name="video"
                        value="yes"
                        checked={formData.include_video === 'yes'}
                        onChange={(e) => handleInputChange('include_video', e.target.value)}
                        className="h-4 w-4 text-green-600"
                      />
                      <Label htmlFor="video_yes">YES</Label>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="video_no"
                        name="video"
                        value="no"
                        checked={formData.include_video === 'no'}
                        onChange={(e) => handleInputChange('include_video', e.target.value)}
                        className="h-4 w-4 text-green-600"
                      />
                      <Label htmlFor="video_no">NO</Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="video_url"
                        name="video"
                        value="url_link_only"
                        checked={formData.include_video === 'url_link_only'}
                        onChange={(e) => handleInputChange('include_video', e.target.value)}
                        className="h-4 w-4 text-green-600"
                      />
                      <Label htmlFor="video_url">URL LINK ONLY</Label>
                    </div>
                  </div>

                  <div className="mt-3 flex items-center space-x-2">
                    <Checkbox
                      id="video_design"
                      checked={formData.video_design}
                      onCheckedChange={(checked) => handleInputChange('video_design', checked)}
                    />
                    <Label htmlFor="video_design">
                      DESIGN VIDEO <span className="font-bold text-orange-600">£25.99</span>
                    </Label>
                  </div>
                </div>

                {/* Window Posters */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 text-green-600">HOW MANY WINDOW POSTERS WOULD YOU LIKE?</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="posters_1"
                        name="posters"
                        value="1"
                        checked={formData.window_posters === '1'}
                        onChange={(e) => handleInputChange('window_posters', e.target.value)}
                        className="h-4 w-4 text-green-600"
                      />
                      <Label htmlFor="posters_1">1 INCLUDED</Label>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="posters_2"
                        name="posters"
                        value="2"
                        checked={formData.window_posters === '2'}
                        onChange={(e) => handleInputChange('window_posters', e.target.value)}
                        className="h-4 w-4 text-green-600"
                      />
                      <Label htmlFor="posters_2">2 <span className="font-bold">£2.99</span></Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="posters_3"
                        name="posters"
                        value="3"
                        checked={formData.window_posters === '3'}
                        onChange={(e) => handleInputChange('window_posters', e.target.value)}
                        className="h-4 w-4 text-green-600"
                      />
                      <Label htmlFor="posters_3">3 <span className="font-bold">£5.99</span></Label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Review & Payment */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold mb-4 text-green-600">REVIEW YOUR ORDER</h3>
                
                {/* Order Summary */}
                <div className="bg-gray-50 p-6 rounded-lg space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-2">Business Information</h4>
                      <p><strong>Business:</strong> {formData.business_name}</p>
                      <p><strong>Contact:</strong> {formData.main_contact}</p>
                      <p><strong>Email:</strong> {formData.email_address}</p>
                      <p><strong>Phone:</strong> {formData.mobile_phone}</p>
                      <p><strong>Address:</strong> {formData.address}, {formData.postcode}</p>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-800 mb-2">Selected Package</h4>
                      <p><strong>Package:</strong> {packageOptions.find(pkg => pkg.id === formData.selected_package)?.name || 'None selected'}</p>
                      <p><strong>URL:</strong> {formData.generated_url}</p>
                      <p><strong>Video:</strong> {formData.include_video}</p>
                      <p><strong>Posters:</strong> {formData.window_posters}</p>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Discount Offers</h4>
                    <div className="flex flex-wrap gap-2">
                      {discountTypes.map(discount => 
                        formData[discount.field] && (
                          <Badge key={discount.id} variant="outline" className="bg-green-100 border-green-300">
                            {discount.label}
                          </Badge>
                        )
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-2">
                      <strong>Method:</strong> {formData.discount_type} | <strong>Details:</strong> {formData.discount_details}
                    </p>
                  </div>
                </div>

                {/* Cost Breakdown */}
                <div className="bg-white border-2 border-green-200 p-6 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-4">COST BREAKDOWN</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Package:</span>
                      <span className="font-semibold">
                        {typeof formData.package_amount === 'number' ? `£${formData.package_amount.toFixed(2)}` : 'TBQ'}
                      </span>
                    </div>
                    
                    {formData.video_design && (
                      <div className="flex justify-between">
                        <span>Design Video:</span>
                        <span className="font-semibold">£25.99</span>
                      </div>
                    )}
                    
                    {formData.window_posters === '2' && (
                      <div className="flex justify-between">
                        <span>Extra Window Poster:</span>
                        <span className="font-semibold">£2.99</span>
                      </div>
                    )}
                    
                    {formData.window_posters === '3' && (
                      <div className="flex justify-between">
                        <span>2 Extra Window Posters:</span>
                        <span className="font-semibold">£5.99</span>
                      </div>
                    )}
                    
                    <hr className="my-3" />
                    <div className="flex justify-between text-lg font-bold text-green-600">
                      <span>TOTAL:</span>
                      <span>
                        {typeof calculateTotal() === 'number' ? `£${calculateTotal().toFixed(2)}` : 'TBQ'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Payment Method */}
                <div>
                  <Label>PAYMENT METHOD</Label>
                  <Select value={formData.payment_method} onValueChange={(value) => handleInputChange('payment_method', value)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select payment method" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="card">CARD</SelectItem>
                      <SelectItem value="send_payment_link">SEND PAYMENT LINK</SelectItem>
                      <SelectItem value="invoice">INVOICE (For Bespoke Packages)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                  <div className="flex items-start space-x-3">
                    <MessageCircle className="h-5 w-5 text-orange-600 mt-1" />
                    <div>
                      <h4 className="font-semibold text-orange-600">What happens next?</h4>
                      <ul className="text-sm text-gray-700 mt-2 space-y-1">
                        <li>• Our technical team will review your application</li>
                        <li>• We'll create your personalized AI chatbot</li>
                        <li>• Generate your unique *.shoppersdiscount.club URL</li>
                        <li>• Create QR codes and window posters</li>
                        <li>• Send you setup instructions and materials</li>
                        <li>• Process payment via your selected method</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Success */}
            {currentStep === 4 && (
              <div className="text-center space-y-6 py-8">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="h-12 w-12 text-green-600" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">Order Submitted Successfully!</h3>
                  <p className="text-gray-600">
                    Thank you for your order. Our team will begin processing your chatbot and marketing materials immediately.
                  </p>
                </div>
                <div className="bg-green-50 p-6 rounded-lg border border-green-200 text-left">
                  <h4 className="font-semibold text-green-600 mb-3">Your Order Details:</h4>
                  <div className="space-y-2 text-sm">
                    <p><strong>Business:</strong> {formData.business_name}</p>
                    <p><strong>Generated URL:</strong> https://{formData.generated_url}</p>
                    <p><strong>Total Amount:</strong> £{calculateTotal().toFixed(2)}</p>
                    <p><strong>Payment Method:</strong> {formData.payment_method}</p>
                    <p><strong>Status:</strong> <Badge className="bg-orange-500 text-white">Processing</Badge></p>
                  </div>
                  <div className="mt-4 p-3 bg-white rounded border">
                    <p className="text-xs text-gray-600">
                      <strong>Reference:</strong> SDC-{Date.now().toString().slice(-6)}<br/>
                      <strong>EPA Number:</strong> Will be assigned upon approval
                    </p>
                  </div>
                </div>
                <Button onClick={onClose} className="bg-green-600 hover:bg-green-700 text-white">
                  Close
                </Button>
              </div>
            )}

            {/* Navigation Buttons */}
            {currentStep < 4 && (
              <div className="flex justify-between pt-6 border-t">
                <Button 
                  variant="outline" 
                  onClick={prevStep}
                  disabled={currentStep === 1}
                  className="border-green-600 text-green-600 hover:bg-green-50"
                >
                  Previous
                </Button>
                
                {currentStep < 3 ? (
                  <Button 
                    onClick={nextStep} 
                    className="bg-green-600 hover:bg-green-700 text-white"
                    disabled={currentStep === 1 && (!formData.business_name || !formData.email_address || !formData.main_contact)}
                  >
                    Next <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                ) : (
                  <Button 
                    onClick={handleSubmit}
                    disabled={isSubmitting || !formData.selected_package || !formData.payment_method}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    {isSubmitting ? 'Submitting Order...' : 'Submit Order'}
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BusinessApplication;